import { Divider, styled, Typography } from '@mui/material';
import { IntervalEntryConfig, IntervalEntryKind } from 'api/report/types';
import { IntervalSummary } from 'report/logic/interval/calculations';
import { formatIntervalKind, formatMinutes } from './formatting';

const Summary = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    borderRadius: 16,
    userSelect: 'none',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1.5, 2),
}));

const SummaryGroup = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
}));

const SummaryLine = styled('div')(({ theme }) => ({
    'display': 'flex',
    'alignItems': 'baseline',
    'justifyContent': 'space-between',
    'gap': theme.spacing(2),
    'fontVariantNumeric': 'tabular-nums',
    '& > :last-child': { whiteSpace: 'nowrap' },
}));

const AdditionalSummaryLine = styled(SummaryLine)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

interface Props {
    summary: IntervalSummary;
    entryKinds: IntervalEntryConfig[];
}

export function ReportSummary({ summary, entryKinds }: Props) {
    const kindSummaries = Object.entries(summary.byKind).flatMap(
        ([kind, { minutes, count }]) =>
            minutes === null
                ? []
                : [{ kind: kind as IntervalEntryKind, minutes, count }]
    );

    return (
        <Summary>
            {kindSummaries.length > 0 && (
                <>
                    <SummaryGroup>
                        {kindSummaries.map(({ kind, minutes, count }) => (
                            <SummaryLine key={kind}>
                                <Typography variant='body2'>
                                    {formatIntervalKind(kind, entryKinds)}
                                    {` (${count})`}
                                </Typography>
                                <Typography variant='body2'>
                                    {formatMinutes(minutes)}
                                </Typography>
                            </SummaryLine>
                        ))}
                    </SummaryGroup>
                    <Divider sx={{ borderColor: 'grey.200' }} />
                </>
            )}
            <SummaryGroup>
                <SummaryLine>
                    <Typography variant='body2' fontWeight={700}>
                        Totalt
                    </Typography>
                    <Typography variant='body2' fontWeight={700}>
                        {formatMinutes(summary.totalMinutes)}
                    </Typography>
                </SummaryLine>
                {summary.sickMinutes > 0 && (
                    <AdditionalSummaryLine>
                        <Typography variant='body2'>Sjuktimmar</Typography>
                        <Typography variant='body2'>
                            {formatMinutes(summary.sickMinutes)}
                        </Typography>
                    </AdditionalSummaryLine>
                )}
                {summary.totalDistanceKm > 0 && (
                    <AdditionalSummaryLine>
                        <Typography variant='body2'>Körsträcka</Typography>
                        <Typography variant='body2'>
                            {summary.totalDistanceKm.toLocaleString('sv-SE')} km
                        </Typography>
                    </AdditionalSummaryLine>
                )}
            </SummaryGroup>
        </Summary>
    );
}
